import { getWebGpuSupport } from "./webgpu-support";

export function syncCanvasPixels(canvas: HTMLCanvasElement): boolean {
  const dpr = Math.min(window.devicePixelRatio, 2);
  const width = Math.max(1, Math.floor(canvas.clientWidth * dpr));
  const height = Math.max(1, Math.floor(canvas.clientHeight * dpr));
  if (canvas.width === width && canvas.height === height) {
    return false;
  }
  canvas.width = width;
  canvas.height = height;
  return true;
}

export interface WebGpuCanvasHandle {
  device: GPUDevice;
  context: GPUCanvasContext;
  format: GPUTextureFormat;
  configure: () => void;
}

export type WebGpuCanvasInitResult =
  | { ok: true; handle: WebGpuCanvasHandle }
  | { ok: false; reason: string };

export async function requestWebGpuCanvas(
  canvas: HTMLCanvasElement,
): Promise<WebGpuCanvasInitResult> {
  const support = getWebGpuSupport(navigator.gpu);
  if (!support.supported) {
    return {
      ok: false,
      reason: support.reason ?? "WebGPU unavailable.",
    };
  }

  let device: GPUDevice;
  try {
    const adapter = await navigator.gpu.requestAdapter();
    if (adapter === null) {
      return { ok: false, reason: "No WebGPU adapter found." };
    }
    device = await adapter.requestDevice();
  } catch (error) {
    return { ok: false, reason: error instanceof Error ? error.message : "WebGPU failed to start." };
  }
  const context = canvas.getContext("webgpu");
  if (context === null) {
    device.destroy();
    return { ok: false, reason: "Could not create WebGPU canvas context." };
  }

  const format = navigator.gpu.getPreferredCanvasFormat();
  const configure = (): void => {
    context.configure({
      device,
      format,
      alphaMode: "opaque",
    });
  };

  syncCanvasPixels(canvas);
  configure();

  return {
    ok: true,
    handle: { device, context, format, configure },
  };
}

export async function destroyWebGpuCanvas(
  handle: WebGpuCanvasHandle,
): Promise<void> {
  try {
    await handle.device.queue.onSubmittedWorkDone();
  } catch {
    // Device may already be lost during teardown.
  }
  handle.context.unconfigure();
  handle.device.destroy();
}

/** Reconfigure after backing-store resize; call from ResizeObserver / window resize. */
export function resizeWebGpuCanvas(
  canvas: HTMLCanvasElement,
  handle: WebGpuCanvasHandle,
): void {
  if (syncCanvasPixels(canvas)) {
    handle.configure();
  }
}

export function watchCanvasResize(
  canvas: HTMLCanvasElement,
  onResize: () => void,
): () => void {
  const resizeObserver = new ResizeObserver(onResize);
  resizeObserver.observe(canvas);
  window.addEventListener("resize", onResize);
  return () => {
    resizeObserver.disconnect();
    window.removeEventListener("resize", onResize);
  };
}
